import { RequestFailure } from '@owl-nest/api-client'
import { Status } from '@owl-nest/redux-wrapper'

import * as api from '@owl-nest/api-client/latest'

import { TYPE, Action } from '../actions/comments'

export type CommentList = {
  parentId: number
  comments: api.Comment<false>[]
  newComments: api.Comment<boolean>[]
  size: number
  next: string | null
}

export type State = {
  cacheId?: string
  list: {
    [s in number]?: CommentList
  } & {
    root?: {
      parentId: 'root'
      comments: api.Comment<true>[]
      newComments: api.Comment<boolean>[]
      size: number
      next: string | null
    }
  }
  status: {
    loadRoot:
      | { status: Status.PRISTINE | Status.PENDING | Status.SUCCESS }
      | { status: Status.FAILURE; failure: RequestFailure }
    createRoot:
      | { status: Status.PRISTINE | Status.PENDING | Status.SUCCESS }
      | { status: Status.FAILURE; failure: RequestFailure }
    loadReply:
      | { status: Status.PRISTINE | Status.PENDING | Status.SUCCESS }
      | { status: Status.FAILURE; failure: RequestFailure }
    createReply:
      | { status: Status.PRISTINE | Status.PENDING | Status.SUCCESS }
      | { status: Status.FAILURE; failure: RequestFailure }
    delete:
      | { status: Status.PRISTINE | Status.PENDING | Status.SUCCESS }
      | { status: Status.FAILURE; failure: RequestFailure }
  }
}

const initialState: State = {
  cacheId: undefined,
  list: {},
  status: {
    loadRoot: { status: Status.PRISTINE },
    createRoot: { status: Status.PRISTINE },
    createReply: { status: Status.PRISTINE },
    loadReply: { status: Status.PRISTINE },
    delete: { status: Status.PRISTINE },
  },
}

export function reducer(state = initialState, action: Action): State {
  switch (action.type) {
    case TYPE.LOAD_ROOT_REQUEST: {
      return {
        ...state,
        status: {
          ...state.status,
          loadRoot: { status: Status.PENDING },
        },
      }
    }
    case TYPE.LOAD_ROOT_FAILURE: {
      return {
        ...state,
        status: {
          ...state.status,
          loadRoot: { status: Status.FAILURE, failure: action.failure },
        },
      }
    }
    case TYPE.LOAD_ROOT_SUCCESS: {
      const rootList = state.list.root
      if (rootList === undefined || action.success.cacheId !== state.cacheId || action.success.forceClean) {
        const list: State['list'] = {
          root: {
            parentId: 'root',
            comments: action.success.comments,
            newComments: [],
            next: action.success.meta.next,
            size: action.success.size,
          },
        }
        for (const comment of action.success.comments) {
          const lastReply = comment.replies?.[comment.replies?.length - 1]
          const cursor = lastReply ? new Date(lastReply?.submit_date).getTime() : Date.now()
          list[comment.id] = {
            parentId: comment.id,
            comments: comment.replies || [],
            newComments: [],
            // when computing the cursor for the replies we use ceil instead of floor
            // because the order is reversed (older to newer).
            // If we have the last comment at x.300s, using floor would put the cursor at
            // x, and we would reload this last comment (since x.300 comes before the cursor in older to newer order).
            // using ceil put the cusor at x+1, wich comes after x.300, so we wont have double load.
            next: `?limit=${10}&since=${Math.ceil(cursor / 1000)}`,
            size: comment.replies_count,
          }
        }

        return {
          ...state,
          cacheId: action.success.cacheId,
          list,
          status: {
            ...state.status,
            loadRoot: { status: Status.SUCCESS },
          },
        }
      } else {
        const list: State['list'] = {
          ...state.list,
          root: {
            ...rootList,
            comments: [...rootList.comments, ...action.success.comments],
            next: action.success.meta.next,
          },
        }
        for (const comment of action.success.comments) {
          const lastReply = comment.replies?.[comment.replies?.length - 1]
          const cursor = lastReply ? new Date(lastReply?.submit_date).getTime() : Date.now()
          list[comment.id] = {
            parentId: comment.id,
            comments: comment.replies || [],
            newComments: [],
            // when computing the cursor for the replies we use ceil instead of floor
            // because the order is reversed (older to newer).
            // If we have the last comment at x.300s, using floor would put the cursor at
            // x, and we would reload this last comment (since x.300 comes before the cursor in older to newer order).
            // using ceil put the cusor at x+1, wich comes after x.300, so we wont have double load.
            next: `?limit=${10}&since=${Math.ceil(cursor / 1000)}`,
            size: comment.replies_count,
          }
        }

        return {
          ...state,
          list,
          status: {
            ...state.status,
            loadRoot: { status: Status.SUCCESS },
          },
        }
      }
    }

    case TYPE.CREATE_ROOT_REQUEST: {
      return {
        ...state,
        status: {
          ...state.status,
          createRoot: { status: Status.PENDING },
        },
      }
    }
    case TYPE.CREATE_ROOT_FAILURE: {
      return {
        ...state,
        status: {
          ...state.status,
          createRoot: { status: Status.FAILURE, failure: action.failure },
        },
      }
    }
    case TYPE.CREATE_ROOT_SUCCESS: {
      const rootList = state.list.root
      if (rootList === undefined) {
        throw Error("You can't create root comments if root list was not loaded first")
      }

      return {
        ...state,
        list: {
          ...state.list,
          root: {
            ...rootList,
            newComments: [action.success, ...rootList.newComments],
          },
          [action.success.id]: {
            parentId: action.success.id,
            comments: [],
            newComments: [],
            // when computing the cursor for the replies we use ceil instead of floor
            // because the order is reversed (older to newer).
            // If we have the last comment at x.300s, using floor would put the cursor at
            // x, and we would reload this last comment (since x.300 comes before the cursor in older to newer order).
            // using ceil put the cusor at x+1, wich comes after x.300, so we wont have double load.
            next: `?limit=${10}&since=${Math.ceil(new Date(action.success.submit_date).getTime() / 1000)}`,
            size: 0,
          },
        },
        status: {
          ...state.status,
          createRoot: { status: Status.SUCCESS },
        },
      }
    }

    case TYPE.LOAD_REPLY_REQUEST: {
      return {
        ...state,
        status: {
          ...state.status,
          loadReply: { status: Status.PENDING },
        },
      }
    }
    case TYPE.LOAD_REPLY_FAILURE: {
      return {
        ...state,
        status: {
          ...state.status,
          loadReply: { status: Status.FAILURE, failure: action.failure },
        },
      }
    }
    case TYPE.LOAD_REPLY_SUCCESS: {
      const commentList = state.list[action.success.parentId]
      if (commentList === undefined) {
        throw Error("You can't load replies for a comment that was not loaded first")
      }

      return {
        ...state,
        list: {
          ...state.list,
          [action.success.parentId]: {
            ...commentList,
            comments: [...commentList.comments, ...action.success.comments],
            // when we load new replies, we are bound to load newly created comments
            // (because replies are ordered older to newer). When this happens, we
            // need to remove them from the `newComments` list.
            newComments: commentList.newComments.filter(comment => {
              return action.success.comments.every(loadedComment => loadedComment.id !== comment.id)
            }),
            next: action.success.meta.next,
          },
        },
        status: {
          ...state.status,
          createReply: { status: Status.SUCCESS },
        },
      }
    }

    case TYPE.CREATE_REPLY_REQUEST: {
      return {
        ...state,
        status: {
          ...state.status,
          createReply: { status: Status.PRISTINE },
        },
      }
    }
    case TYPE.CREATE_REPLY_FAILURE: {
      return {
        ...state,
        status: {
          ...state.status,
          createReply: { status: Status.FAILURE, failure: action.failure },
        },
      }
    }
    case TYPE.CREATE_REPLY_SUCCESS: {
      const commentList = state.list[action.success.parentId]
      if (commentList === undefined) {
        throw Error("You can't create replies for a comment that was not loaded first")
      }

      return {
        ...state,
        list: {
          ...state.list,
          [action.success.parentId]: {
            ...commentList,
            newComments: [...commentList.newComments, action.success],
          },
        },
        status: {
          ...state.status,
          createReply: { status: Status.SUCCESS },
        },
      }
    }

    case TYPE.DELETE_REQUEST: {
      return {
        ...state,
        status: {
          ...state.status,
          delete: { status: Status.PENDING },
        },
      }
    }
    case TYPE.DELETE_FAILURE: {
      return {
        ...state,
        status: {
          ...state.status,
          delete: { status: Status.FAILURE, failure: action.failure },
        },
      }
    }
    case TYPE.DELETE_SUCCESS: {
      let nextState = state

      const parentCommentList = state.list[action.success.parentId]
      if (parentCommentList !== undefined) {
        nextState = {
          ...nextState,
          list: {
            ...nextState.list,
            [action.success.parentId]: {
              ...parentCommentList,
              comments: (parentCommentList.comments as api.Comment[]).filter(
                comment => comment.id !== action.success.id,
              ),
              newComments: (parentCommentList.newComments as api.Comment[]).filter(
                comment => comment.id !== action.success.id,
              ),
            },
          },
        }
      }

      delete nextState.list[action.success.id]

      return {
        ...nextState,
        status: {
          ...state.status,
          delete: { status: Status.SUCCESS },
        },
      }
    }
  }

  return state
}
